import axios from 'axios';
import { API_URL } from 'configs';

/**
 * Sets up Axios interceptors to automatically add object_viewed_id to API requests
 * This function should be called once after the Redux store is created
 * @param {Function} getState - Redux getState function to access current state
 */
export const setupAxiosInterceptors = (getState) => {
  // Request interceptor to add object_viewed_id
  axios.interceptors.request.use(
    (config) => {
      try {
        const state = getState();
        const object_viewed_id = state?.session?.current_object_viewed_id;
        
        // Only add object_viewed_id if it exists and is not already in the request
        if (object_viewed_id) {
          // For GET requests, add as query parameter
          if (config.method === 'get' || config.method === 'GET') {
            if (!config.params) {
              config.params = {};
            }
            // Only add if not already present
            if (!config.params.object_viewed_id) {
              config.params.object_viewed_id = object_viewed_id;
            }
          }
          // For POST, PUT, PATCH, DELETE requests, add to request body
          else if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
            if (config.data) {
              // FormData is typeof "object"; check it before spreading or the file/title fields are dropped.
              if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
                if (!config.data.has('object_viewed_id')) {
                  config.data.append('object_viewed_id', object_viewed_id);
                }
              } else if (typeof config.data === 'object' && !Array.isArray(config.data)) {
                if (!config.data.object_viewed_id) {
                  config.data = {
                    ...config.data,
                    object_viewed_id: object_viewed_id
                  };
                }
              }
            } else {
              config.data = { object_viewed_id: object_viewed_id };
            }
          }
        }
      } catch (error) {
        console.warn('Error in axios request interceptor:', error);
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling (optional - can be extended)
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      // Handle 401 unauthorized - token might be expired
      if (error.response?.status === 401) {
        // You can dispatch logout action here if needed
        // This would require passing dispatch as well
      }
      return Promise.reject(error);
    }
  );
};

/**
 * Alternative: Setup interceptors using a store reference
 * This version allows access to both getState and dispatch
 * @param {Object} store - Redux store object
 */
export const setupAxiosInterceptorsWithStore = (store) => {
  setupAxiosInterceptors(() => store.getState());
};
